<script>
import primeLogo from '@/assets/prime.png';

export default {
    name: 'PacklistUpsell',
    props: {
        upsellProducts: {
            type: Array,
            default: () => []
        },
        packlistName: {
            type: String,
            default: null
        },
        currentFyler: {
            type: Object,
            default: null
        },
        currentPackstats: {
            type: Object,
            default: () => ({
                progress: 0,
                targetProgress: 40
            })
        },
        currentPacklistItem: {
            type: Object,
            default: null
        },
        currentPrinter: {
            type: Object,
            default: null
        },
        openItems: {
            type: Array,
            default: () => []
        },
        isFullscreen: {
            type: Boolean,
            default: false
        },
        packagingOptions: {
            type: Array,
            default: () => []
        },
        showPackagingOptions: {
            type: Boolean,
            default: false
        },
        packagingQuantity: {
            type: Number,
            default: 1
        },
        selectedPackagingOption: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            primeLogo,
            currentTime: Date.now(),
            sessionTimer: null
        };
    },
    computed: {
        hasUpsellProducts() {
            return this.upsellProducts && this.upsellProducts.length > 0;
        },
        hasPackagingOptions() {
            return this.packagingOptions && this.packagingOptions.length > 0;
        },
        displayedPackagingOption() {
            // Show selected packaging if available, otherwise fall back to first option
            return this.selectedPackagingOption || (this.hasPackagingOptions ? this.packagingOptions[0] : null);
        },
        customer() {
            return this.currentPacklistItem?.customer || {};
        },
        isPrime() {
            return this.currentPacklistItem?.shipping?.type === 'PRIME';
        },
        getProgressColor() {
            return this.currentPackstats.progress <= this.currentPackstats.targetProgress ? 'success' : 'error';
        },
        sessionTimeElapsed() {
            if (!this.currentPackstats.packStart) return '00:00';
            
            const now = new Date(this.currentTime);
            const packStart = new Date(this.currentPackstats.packStart);
            const elapsed = Math.floor((now - packStart) / 1000);
            return this.formatTime(elapsed);
        },
        calculatePacksPerHour() {
            if (!this.currentPackstats.packStart) return 0;
            
            const now = new Date(this.currentTime);
            const packStart = new Date(this.currentPackstats.packStart);
            const elapsed = (now - packStart) / 1000;
            if (elapsed <= 0) return 0;
            
            const packs = this.currentPackstats.count;
            const packsPerHour = packs / (elapsed / 3600);
            return Math.round(packsPerHour);
        }
    },
    mounted() {
        this.startSessionTimer();
    },
    beforeDestroy() {
        this.stopSessionTimer();
    },
    methods: {
        formatTime(seconds) {
            const minutes = Math.floor(seconds / 60);
            const remainingSeconds = seconds % 60;
            return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
        },
        formatStartTime(timestamp) {
            if (!timestamp) return '';
            const date = new Date(timestamp);
            const hours = date.getHours().toString().padStart(2, '0');
            const minutes = date.getMinutes().toString().padStart(2, '0');
            return `${hours}:${minutes} Uhr`;
        },
        handlePrinterDialog() {
            this.$emit('printer-dialog');
        },
        handleFullscreen() {
            this.$emit('fullscreen');
        },
        startSessionTimer() {
            this.sessionTimer = setInterval(() => {
                this.currentTime = Date.now();
            }, 1000);
        },
        stopSessionTimer() {
            if (this.sessionTimer) {
                clearInterval(this.sessionTimer);
                this.sessionTimer = null;
            }
        },
        openPackagingOptionsDialog() {
            this.$emit('open-packaging-options-dialog');
        }
    },
    watch: {
        packagingOptions: {
            immediate: true,
            handler(newOptions) {
                // console.log('PacklistUpsell - packagingOptions changed:', newOptions);
            }
        }
    }
}
</script>

<template>
    <div>
        <!-- Products and Address Row -->
        <div class="d-flex gap-4 justify-space-between pa-3 white" v-if="upsellProducts.length > 0 || currentFyler || (hasPackagingOptions && showPackagingOptions)">
            <!-- Products Section (Left) -->
            <div class="d-flex gap-4 products-upsell-list">
                <!-- Upsell Products -->
                <v-card
                    v-for="product in upsellProducts"
                    :key="product.sku"
                    class="product-card pa-1 flex-shrink-1"
                    :style="{ width: `${100 / Math.min(upsellProducts.length, 8)}%`, aspectRatio: '1/1' }"
                    elevation="2"
                >
                    <v-img
                        :src="`/api/files/sku?sku=${product.sku}`"
                        contain
                        class="product-image"
                        height="100%"
                        width="100%"
                    >
                        <v-badge
                            v-if="product.qty > 1"
                            :content="`Menge: ${product.qty}`"
                            color="error"
                            class="quantity-badge"
                            location="top end"
                            offset-x="-4"
                            offset-y="8"
                        ></v-badge>
                    </v-img>  
                </v-card>
                
                <!-- Flyer Card -->
                <v-card 
                    v-if="currentFyler"
                    class="product-card pa-1 flex-shrink-0"
                    :style="{ width: `${100 / Math.min(upsellProducts.length, 8)}%`, aspectRatio: '1/1', backgroundColor: currentFyler?.bg, color: currentFyler?.color }"
                    elevation="2"
                >
                    <v-img
                        :src="currentFyler.url"
                        contain
                        height="100%"
                        width="100%"
                    ></v-img>
                </v-card>
                
                <!-- Fallback message when no upsell products -->
                <div v-if="!hasUpsellProducts && !currentFyler" class="no-upsell">
                    <span class="text-caption no-upsell-text">Keine Zusatzprodukte verfügbar</span>
                </div>
            </div>

            <!-- Cartons Section (Right) -->
            <div class="address-section" v-if="currentPacklistItem && showPackagingOptions">
                <v-card 
                    elevation="2" 
                    class="h-100 packaging-card" 
                    @click="openPackagingOptionsDialog"
                    hover
                >
                    <v-card-text class="text-subtitle-1 pa-3">
                        <div v-if="hasPackagingOptions && displayedPackagingOption">
                            <!-- Title with ellipsis for long names -->
                            <div class="d-flex align-center">
                                <div class="packaging-title font-weight-medium text-truncate-2-lines mr-1">
                                    {{ displayedPackagingOption.title || displayedPackagingOption.sku }}
                                </div>
                                <v-chip v-if="packagingQuantity > 1" x-small color="primary" class="flex-shrink-0 quantity-chip">
                                    {{ packagingQuantity }}×
                                </v-chip>
                            </div>
                            
                            <!-- Dimensions -->
                            <div class="packaging-dimensions text-caption mt-2">
                                {{ displayedPackagingOption.lengthCm }} × {{ displayedPackagingOption.widthCm }} × {{ displayedPackagingOption.heightCm }} cm
                            </div>
                        </div>
                        <div v-else class="text-center py-2">
                            <span class="text-subtitle-2">Kartonage auswählen</span>
                        </div>
                    </v-card-text>
                </v-card>
            </div>
        </div>

        <!-- Enhanced Time Statistics Section -->
        <div class="time-stats-container">
            <div class="packlist-header mb-2 d-flex align-center justify-space-between">
                <div>
                    <v-chip color="primary" small>{{ packlistName }}</v-chip>
                    <span class="ml-2 text-body-2">{{ openItems.length }} offen</span>
                </div>
                <div class="d-flex align-center">
                    <v-chip v-if="currentPrinter" size="x-small" outlined color="grey" class="mr-2">
                        <v-icon x-small left>mdi-printer</v-icon>
                        {{ currentPrinter.name }}
                    </v-chip>
                    <v-btn icon x-small @click="handlePrinterDialog" class="mr-1">
                        <v-icon small>mdi-cog</v-icon>
                    </v-btn>
                    <v-btn icon x-small @click="handleFullscreen">
                        <v-icon small v-if="isFullscreen">mdi-fullscreen-exit</v-icon>
                        <v-icon small v-else>mdi-fullscreen</v-icon>
                    </v-btn>
                </div>
            </div>
            <div class="time-tracking d-flex align-center">
                <v-progress-linear
                    :value="Math.min((currentPackstats.progress / currentPackstats.targetProgress) * 100, 100)"
                    :color="getProgressColor"
                    height="20"
                    class="time-progress"
                    :class="{ 'pulse': currentPackstats.progress > currentPackstats.targetProgress }"
                    style="width: 120px;"
                >
                    <template v-slot:default>
                        <span class="white--text font-weight-medium">{{ formatTime(currentPackstats.progress) }}</span>
                    </template>
                </v-progress-linear>
                <div v-if="currentPackstats.packStart" class="session-time-container mx-2">
                    {{ sessionTimeElapsed }}
                </div>
                <v-divider vertical class="mx-2 my-0" style="height: 24px"></v-divider>
                <div class="stats-inline">
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip size="x-small" color="success" v-bind="attrs" v-on="on" class="mr-2">
                                <v-icon x-small left>mdi-timer</v-icon>
                                {{ formatTime(currentPackstats.time) }}
                            </v-chip>
                        </template>
                        <span>Durchschnittliche Packzeit</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                        <template v-slot:activator="{ on, attrs }">
                            <v-chip size="x-small" color="info" v-bind="attrs" v-on="on">
                                <v-icon x-small left>mdi-package-variant</v-icon>
                                {{ currentPackstats.count }}
                            </v-chip>
                        </template>
                        <span>Pakete heute verpackt</span>
                    </v-tooltip>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.gap-4 {
    gap: 16px;
}

.products-upsell-list {
    min-width: 0;
    flex-wrap: nowrap;
    width: 80%;
}

.product-card {
    transition: transform 0.2s ease;
    aspect-ratio: 1/1;
    display: flex;
    flex-direction: column;
    min-width: 80px;
    max-width: 120px;
    position: relative;
}

.product-card:hover {
    transform: scale(1.05);
}

.product-image {
    object-fit: contain;
    background-color: white;
    height: 100%;
    width: 100%;
}

.quantity-badge :deep(.v-badge__content) {
    font-size: 12px;
    padding: 4px 8px;
    min-width: auto;
    height: auto;
}

.no-upsell {
    position: relative;
    padding: 0.75rem;
    background-color: #f5f5f5;
    border-radius: 4px;
    text-align: center;
    color: #757575;
    width: 100%;
}

.no-upsell-text {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
}

.address-section {
    background-color: transparent;
    width: 20%;
}

.h-100 {
    height: 100%;
}

.w-50 {
    width: 50%;
}

.customer-name, .customer-address {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
}

.customer-name {
    margin-bottom: 4px;
}

.time-stats-container {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding: 8px 16px;
    background-color: #f5f5f5;
    border-radius: 0 0 4px 4px;
}

.time-progress {
    border-radius: 4px;
    overflow: hidden;
    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.stats-inline {
    display: flex;
    align-items: center;
}

.session-time-container {
    background-color: #3949ab;
    color: white;
    border-radius: 4px;
    padding: 4px 8px;
    font-weight: 600;
    font-size: 0.9rem;
    min-width: 60px;
    text-align: center;
    box-shadow: 0 1px 2px rgba(0,0,0,0.15);
}

.printer-chip {
    transition: all 0.2s ease;
}

/* Pulsing animation for when time exceeds target */
.pulse {
    animation: pulse-effect 1.5s infinite;
}

@keyframes pulse-effect {
    0% {
        opacity: 1;
    }
    50% {
        opacity: 0.7;
    }
    100% {
        opacity: 1;
    }
}

.packaging-card {
    background-color: #f5f5f5;
    transition: all 0.2s ease;
}

.packaging-card:hover {
    background-color: #e8f5e9;
}

.packaging-title {
    color: #2196F3;
    font-size: 1rem;
    flex: 1;
    min-width: 0; /* To enable flex shrinking */
}

.packaging-dimensions {
    color: #757575;
}

.quantity-chip {
    flex-shrink: 0;
    margin-left: 4px;
    height: auto !important;
    min-width: auto !important;
}

.text-truncate-2-lines {
    display: -webkit-box;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
    max-height: 2.4em; /* 2 lines * 1.2 line-height */
    word-break: break-word;
}
</style>